// ─── El IVA de cada línea ────────────────────────────────────────────────────
// Antes el IVA se calculaba UNA vez sobre el subtotal entero, con la tarifa
// general: toda venta era gravada al 19%. Eso no permite vender un bien que la
// ley no grava, y en Colombia hay dos formas distintas de no llevar IVA:
//
//   • EXCLUIDO (art. 424 E.T.) — el bien "no causa el impuesto". Quien solo
//     vende excluidos no es responsable de IVA, y el IVA que pagó al comprar
//     ese bien NO es descontable: va como mayor costo.
//
//   • EXENTO (art. 477 E.T.) — el bien SÍ está gravado, pero a tarifa CERO. La
//     diferencia está en los descontables: el derecho a descontarlos y pedir
//     devolución es solo del PRODUCTOR del bien exento (art. 440) y del
//     exportador. Un simple comercializador vende sin IVA y sin derecho a
//     descontables, igual que si fuera excluido.
//
// En la factura las dos se cobran en cero, pero son bases distintas y se
// declaran aparte, así que el ERP las distingue en vez de tratarlas como "0%".
//
// CÓMO LO USA MATHIZ. El régimen es del BIEN, no de la operación, y por eso la
// compra y la venta pueden llevar IVA distinto: son bienes distintos. Se compra
// al proveedor una "Matera cerámica" (gravada) y una "Mata suculenta" (excluida,
// partida 06.02), y se le vende al cliente UN artículo, "Suculenta", que es el
// producto terminado y va gravado al 19% porque se transformó. Tres productos
// en el catálogo, tres clasificaciones, y cada documento toma la del bien que
// mueve. Nada de esto necesita una regla por proveedor ni por documento.
//
// Ojo con el art. 490: si la empresa vende gravado Y excluido a la vez, el IVA
// de los costos comunes que no se pueda imputar a una operación concreta hay
// que PRORRATEARLO, y la parte de las excluidas se lleva al costo. Eso no lo
// puede decidir el ERP línea por línea —depende de la declaración del período—
// pero sí avisa cuando la situación aparece (ver avisoProrrateo).
import { getParametrosFiscales, ivaPct } from "./cloudSync.js";
import { state } from "./state.js";
import { parseNumber } from "./utils.js";

export const IVA_GRAVADO  = "gravado";
export const IVA_EXENTO   = "exento";
export const IVA_EXCLUIDO = "excluido";

// Lo que se ofrece al clasificar un producto. La tarifa del 5% es la del
// art. 468-1 (bienes) / 468-3 (servicios); la general vive en Parámetros
// Fiscales para no quemar el 19 aquí.
export function opcionesIVA() {
  const general = ivaPct();
  return [
    { id: IVA_GRAVADO,  pct: general, lbl: `Gravado ${general}% (tarifa general)`,
      ayuda: "Art. 468 E.T. Lo normal: el producto causa IVA a la tarifa general." },
    { id: IVA_GRAVADO,  pct: 5,       lbl: "Gravado 5% (tarifa diferencial)",
      ayuda: "Art. 468-1 E.T. para bienes y 468-3 para servicios." },
    { id: IVA_EXENTO,   pct: 0,       lbl: "Exento — tarifa 0% (art. 477)",
      ayuda: "Gravado a tarifa cero. El derecho a descontables y devolución es solo del PRODUCTOR del bien (art. 440) y del exportador; un comercializador vende sin IVA y sin descontables." },
    { id: IVA_EXCLUIDO, pct: 0,       lbl: "Excluido — no causa IVA (art. 424)",
      ayuda: "El bien no causa el impuesto: el proveedor no lo cobra y tampoco se le cobra al cliente. Es el caso de las materias primas que Mathiz compra sin IVA, como las plantas vivas (partidas 06.01 y 06.02). Si esa materia prima se vende TRANSFORMADA, el producto terminado es otro artículo del catálogo y va gravado." }
  ];
}

// Valor con el que se guarda la elección en un <select>: tipo y tarifa juntos,
// porque "gravado" no dice nada sin su porcentaje.
export function valorOpcion(o) { return `${o.id}:${o.pct}`; }

export function leerValorOpcion(v) {
  const [tipo, pct] = String(v || "").split(":");
  const t = [IVA_GRAVADO, IVA_EXENTO, IVA_EXCLUIDO].includes(tipo) ? tipo : IVA_GRAVADO;
  const p = parseFloat(pct);
  return { iva_tipo: t, iva_pct: t === IVA_GRAVADO ? (isNaN(p) ? null : p) : 0 };
}

function norm(s) {
  return String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/\s+/g, " ").trim();
}

// La ficha del producto de una línea. Las líneas guardan el NOMBRE del
// artículo, no su id, así que se busca por nombre con la misma tolerancia que
// el resto del ERP (mayúsculas, tildes, espacios de más).
export function productoDeArticulo(nombre) {
  const prods = state.data.productos || [];
  if (!nombre) return null;
  const exacto = prods.find(p => p.nombre === nombre);
  if (exacto) return exacto;
  const n = norm(nombre);
  return prods.find(p => norm(p.nombre) === n) || null;
}

// El régimen de un producto del maestro. Sin clasificar = gravado a la tarifa
// general: es lo que eran los 97 productos que ya existían.
export function regimenDeProducto(prod) {
  const tipo = prod && [IVA_EXENTO, IVA_EXCLUIDO].includes(prod.iva_tipo) ? prod.iva_tipo : IVA_GRAVADO;
  if (tipo !== IVA_GRAVADO) return { tipo, pct: 0 };
  const p = parseFloat(prod && prod.iva_pct);
  return { tipo: IVA_GRAVADO, pct: isNaN(p) ? ivaPct() : p };
}

// El régimen de una LÍNEA de documento, en este orden:
//   1. lo que la propia línea tenga congelado (facturas y notas crédito: son
//      documentos fiscales y no pueden cambiar porque después se reclasifique
//      el producto en el maestro),
//   2. la clasificación del producto en el maestro,
//   3. gravado a la tarifa general (todo lo anterior a esta función).
export function regimenDeLinea(item) {
  if (item && [IVA_EXENTO, IVA_EXCLUIDO].includes(item.iva_tipo)) return { tipo: item.iva_tipo, pct: 0 };
  if (item && item.iva_tipo === IVA_GRAVADO) {
    const p = parseFloat(item.iva_pct);
    return { tipo: IVA_GRAVADO, pct: isNaN(p) ? ivaPct() : p };
  }
  return regimenDeProducto(productoDeArticulo(item && item.articulo));
}

export function baseDeLinea(item) {
  if (!item) return 0;
  const total = parseNumber(item.total);
  if (total) return total;
  return (parseNumber(item.cantidad) || 0) * (parseNumber(item.precio_unitario) || 0);
}

export function etiquetaRegimen(r) {
  if (!r) return "";
  if (r.tipo === IVA_EXENTO) return "Exento";
  if (r.tipo === IVA_EXCLUIDO) return "Excluido";
  return `IVA ${r.pct}%`;
}

// ─── El desglose de un documento ────────────────────────────────────────────
// Una sola pasada por las líneas: la base de cada régimen, el IVA de cada
// tarifa y los totales. Es lo que reemplaza a calcIVA(subtotal) en todas las
// pantallas: sumar el IVA línea por línea es lo único que da el valor correcto
// cuando en el mismo documento hay tarifas distintas.
export function desgloseIVA(items) {
  const grupos = new Map();   // clave tipo:pct → { tipo, pct, base, iva }
  let subtotal = 0, iva = 0;
  (items || []).forEach(it => {
    const base = baseDeLinea(it);
    if (!base) return;
    const r = regimenDeLinea(it);
    const clave = `${r.tipo}:${r.pct}`;
    const ivaLinea = r.tipo === IVA_GRAVADO ? Math.round(base * r.pct / 100) : 0;
    subtotal += base;
    iva += ivaLinea;
    const g = grupos.get(clave) || { tipo: r.tipo, pct: r.pct, base: 0, iva: 0 };
    g.base += base; g.iva += ivaLinea;
    grupos.set(clave, g);
  });
  // El orden es el del Formulario 300: primero las gravadas (de mayor a menor
  // tarifa), después las exentas y al final las excluidas. Dejarlo al alfabeto
  // ponía "Excluido" antes que "Exento" sin ninguna razón.
  const rango = { [IVA_GRAVADO]: 0, [IVA_EXENTO]: 1, [IVA_EXCLUIDO]: 2 };
  const lista = [...grupos.values()].sort((a, b) => rango[a.tipo] - rango[b.tipo] || b.pct - a.pct);
  return {
    subtotal, iva, total: subtotal + iva,
    grupos: lista.map(g => ({ ...g, lbl: etiquetaRegimen(g) })),
    // "Mezclado" es la señal de que hay que MOSTRAR el desglose: con un solo
    // régimen la línea de siempre ("IVA 19%") ya lo dice todo.
    mezclado: lista.length > 1,
    baseGravada:  lista.filter(g => g.tipo === IVA_GRAVADO).reduce((s, g) => s + g.base, 0),
    baseExenta:   lista.filter(g => g.tipo === IVA_EXENTO).reduce((s, g) => s + g.base, 0),
    baseExcluida: lista.filter(g => g.tipo === IVA_EXCLUIDO).reduce((s, g) => s + g.base, 0)
  };
}

// ─── Los renglones de impuesto de un documento ──────────────────────────────
// Devuelve QUÉ imprimir, no cómo: cada pantalla y el PDF le dan su formato. Con
// un solo régimen es la línea de siempre ("IVA 19%"); en cuanto hay mezcla hay
// que mostrar CADA BASE por separado, que es lo que permite verificar el
// impuesto y lo que distingue una base exenta (art. 477) de una excluida
// (art. 424): en pesos las dos van en cero y se verían iguales.
//
// `ivaGuardado` es el valor que el documento trae grabado, para los documentos
// anteriores a la clasificación (sus líneas no tienen régimen y no hay nada que
// desglosar).
export function renglonesIVA(items, ivaGuardado) {
  const d = desgloseIVA(items);
  if (!d.grupos.length) return [{ lbl: `IVA ${ivaPct()}%`, valor: parseNumber(ivaGuardado) || 0 }];
  if (!d.mezclado) {
    const g = d.grupos[0];
    if (g.tipo === IVA_EXENTO)   return [{ lbl: "Base exenta (art. 477 E.T.)", valor: g.base }, { lbl: "IVA", valor: 0 }];
    if (g.tipo === IVA_EXCLUIDO) return [{ lbl: "Base excluida (art. 424 E.T.)", valor: g.base }, { lbl: "IVA", valor: 0 }];
    return [{ lbl: `IVA ${g.pct}%`, valor: g.iva }];
  }
  const out = [];
  d.grupos.forEach(g => {
    if (g.tipo === IVA_EXENTO)        out.push({ lbl: "Base exenta (art. 477 E.T.)", valor: g.base });
    else if (g.tipo === IVA_EXCLUIDO) out.push({ lbl: "Base excluida (art. 424 E.T.)", valor: g.base });
    else {
      out.push({ lbl: `Base gravada ${g.pct}%`, valor: g.base });
      out.push({ lbl: `IVA ${g.pct}%`, valor: g.iva });
    }
  });
  return out;
}

// Las mismas líneas, todas sin IVA. Es lo que es una orden de compra
// INFORMATIVA (OCI): no se causa, así que ninguna línea lleva impuesto, diga lo
// que diga el producto o venga como venga la fila de la nube. Los que impriman
// o muestren una informativa pasan sus líneas por aquí antes de desglosar.
export function itemsSinIVA(items) {
  return (items || []).map(it => ({ ...it, iva_tipo: IVA_EXCLUIDO, iva_pct: 0 }));
}

export function hayNoGravados(items) {
  return (items || []).some(it => baseDeLinea(it) && regimenDeLinea(it).tipo !== IVA_GRAVADO);
}

// ─── Congelar el régimen al emitir ──────────────────────────────────────────
// La factura y la nota crédito guardan en cada línea el régimen con el que se
// emitieron. Si mañana el producto se reclasifica —o cambia la tarifa general—
// el documento ya emitido sigue diciendo lo que dijo ante la DIAN.
export function congelarRegimenItems(items) {
  return (items || []).map(it => {
    const r = regimenDeLinea(it);
    return { ...it, iva_tipo: r.tipo, iva_pct: r.pct };
  });
}

// ─── Aviso del art. 490 ─────────────────────────────────────────────────────
// Vender gravado y excluido a la vez obliga a prorratear el IVA descontable de
// los costos comunes; la parte de las excluidas se va al costo. El cálculo es
// del período y lo hace la contadora sobre la declaración, no el ERP línea por
// línea, pero callarlo sería peor: quien factura no tiene por qué saber que
// acaba de crear esa obligación.
export function avisoProrrateo(items) {
  const d = desgloseIVA(items);
  if (!d.baseExcluida && !d.baseExenta) return "";
  if (!d.baseGravada) return "";
  return "Este documento mezcla operaciones gravadas con excluidas o exentas. " +
    "Al declarar, el IVA de los costos y gastos comunes se debe prorratear " +
    "(art. 490 E.T.): la parte que corresponda a las excluidas no es descontable " +
    "y va como mayor costo. Avísale a contabilidad.";
}

// ¿La empresa se dedica también a vender bienes que no causan IVA? Se usa para
// decidir si vale la pena mostrar la clasificación en las pantallas.
export function catalogoTieneNoGravados() {
  return (state.data.productos || []).some(p => regimenDeProducto(p).tipo !== IVA_GRAVADO);
}

// La tarifa general, reexportada para que nadie tenga que saber que vive en
// cloudSync.
export { ivaPct, getParametrosFiscales };
